// Release management service (CRUD)
import { supabase } from './supabase';
import { noderr, DatabaseError, ValidationError } from '../../shared/utils/errorHandler';
import type { Release, CreateReleaseData, UpdateReleaseData, ReleasesFilter } from '../../shared/types/entities';

export class ReleaseService {
  async list(filter: ReleasesFilter = {}): Promise<Release[]> {
    let query = supabase
      .from('releases')
      .select(`
        *,
        artist:artists(*)
      `)
      .or('archived.is.null,archived.eq.false'); // Only show non-archived releases

    if (filter.artist_id) query = query.eq('artist_id', filter.artist_id);
    if (filter.search) {
      const q = `%${filter.search}%`;
      query = query.ilike('title', q);
    }
    const sortBy = filter.sortBy || 'release_date';
    const sortOrder = filter.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    const [error, result] = await noderr(query);
    if (error) throw new DatabaseError(`Failed to fetch releases: ${error.message}`);
    return ((result as any)?.data || []) as Release[];
  }

  async getById(id: string): Promise<Release | null> {
    const [error, result] = await noderr(
      supabase
        .from('releases')
        .select(`
          *,
          artist:artists(*)
        `)
        .eq('id', id)
        .single()
    );
    if (error) {
      if ((error as any).code === 'PGRST116') return null;
      throw new DatabaseError(`Failed to fetch release: ${error.message}`);
    }
    return (result as any)?.data as Release;
  }

  async create(data: CreateReleaseData): Promise<Release> {
    if (!data.artist_id || data.artist_id.trim() === '') throw new ValidationError('artist_id is required');
    if (!data.title?.trim()) throw new ValidationError('title is required');

    const payload: Partial<CreateReleaseData> = {
      artist_id: data.artist_id,
      title: data.title.trim(),
      release_date: data.release_date || null,
      release_type: data.release_type || 'single',
      track_count: data.track_count ?? 1,
      duration_ms: data.duration_ms ?? 0,
      spotify_url: data.spotify_url || null,
      spotify_id: data.spotify_id || null,
      spotify_data: data.spotify_data || null,
      artwork_url: data.artwork_url || null,
    } as any;

    const [error, result] = await noderr(
      supabase.from('releases').insert(payload).select('*').single()
    );
    
    if (error) {
      console.error('ReleaseService.create - Supabase error:', error);
      throw new DatabaseError(`Failed to create release: ${error.message}`);
    }
    
    return (result as any)?.data as Release;
  }

  async update(id: string, updates: UpdateReleaseData): Promise<Release> {
    const clean: any = {};
    if (updates.artist_id !== undefined) clean.artist_id = updates.artist_id;
    if (updates.title !== undefined) clean.title = updates.title?.trim() || '';
    if (updates.release_date !== undefined) clean.release_date = updates.release_date || null;
    if (updates.release_type !== undefined) clean.release_type = updates.release_type;
    if (updates.track_count !== undefined) clean.track_count = updates.track_count ?? 0;
    if (updates.duration_ms !== undefined) clean.duration_ms = updates.duration_ms ?? 0;
    if (updates.spotify_url !== undefined) clean.spotify_url = updates.spotify_url || null;
    if (updates.spotify_id !== undefined) clean.spotify_id = updates.spotify_id || null;
    if (updates.spotify_data !== undefined) clean.spotify_data = updates.spotify_data || null;
    if (updates.artwork_url !== undefined) clean.artwork_url = updates.artwork_url || null;

    const [error, result] = await noderr(
      supabase.from('releases').update(clean).eq('id', id).select('*').single()
    );
    if (error) throw new DatabaseError(`Failed to update release: ${error.message}`);
    return (result as any)?.data as Release;
  }

  // Archive release (soft delete)
  async archiveRelease(id: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('releases')
        .update({ 
          archived: true 
        })
        .eq('id', id)
    );
    
    if (error) {
      throw new DatabaseError(`Failed to archive release: ${error.message}`);
    }
  }

  // Restore archived release
  async restoreRelease(id: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('releases')
        .update({ 
          archived: false 
        })
        .eq('id', id)
        .eq('archived', true) // Only restore archived releases
    );
    
    if (error) {
      throw new DatabaseError(`Failed to restore release: ${error.message}`);
    }
  }

  // Get archived releases
  async getArchivedReleases(): Promise<Release[]> {
    const [error, result] = await noderr(
      supabase
        .from('releases')
        .select(`
          *,
          artist:artists(*)
        `)
        .eq('archived', true)
        .order('created_at', { ascending: false })
    );
    
    if (error) {
      throw new DatabaseError(`Failed to fetch archived releases: ${error.message}`);
    }
    
    return ((result as any)?.data || []) as Release[];
  }

  // Delete release (permanent - admin only)
  async deleteRelease(id: string): Promise<void> {
    // Check if release has associated Meta campaigns (using internal_campaign_id as release_id)
    const [campaignError, campaignResult] = await noderr(
      supabase
        .from('meta_campaigns')
        .select('id')
        .eq('internal_campaign_id', id)
        .limit(1)
    );

    if (!campaignError && campaignResult && (campaignResult as any)?.data?.length > 0) {
      throw new ValidationError('Cannot delete release with associated campaigns. Delete campaigns first.');
    }

    const [error] = await noderr(
      supabase
        .from('releases')
        .delete()
        .eq('id', id)
    );
    
    if (error) {
      throw new DatabaseError(`Failed to delete release: ${error.message}`);
    }
  }
}

export const releaseService = new ReleaseService();
